/**
 * TimelineGrid Component
 *
 * This component renders the main timeline grid with:
 * - Plot lines (horizontal rows)
 * - Chapters (vertical columns)
 * - Plot cards (cells within the grid)
 * - Context menus for interactions
 * - Drag and drop functionality
 *
 * It supports selection mode for bulk operations and provides
 * a rich set of interactions for managing the timeline structure.
 */

import { motion } from 'framer-motion';
import { Check, CheckSquare, Edit, FileText, MoreHorizontal, Sparkles, Trash2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';

// UI Components
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Hooks
import { useDialogStore } from '@/hooks/useDialog';

// Timeline Components
import { DroppableCell } from './DroppableCell';
import EnhancedContextMenu from './enhanced-context-menu';

// Types
import type { Chapter, PlotCard, PlotLine } from '../types';

// Styles
import '@/styles/plotcard-themes.css';

/**
 * CSS for hover effects on action buttons
 * Makes buttons visible on hover and always visible on touch devices
 */
// Inline styles removed in favor of Tailwind classes
// .action-buttons class is now handled via group-hover in the render method

/**
 * Color definitions for different card types
 * Each type has background, border, hover, and text colors for both light and dark themes
 */



const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const item = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0 }
};

/**
 * Props for the TimelineGrid component
 */
interface TimelineGridProps {
    // Data props
    plotLines: PlotLine[];                // List of plot lines (horizontal rows)
    chapters: Chapter[];                  // List of chapters (vertical columns)
    cards: PlotCard[];                    // List of plot cards to display in the grid

    // Card action handlers
    onCardSelect: (card: PlotCard, isMultiSelect?: boolean) => void;  // Handle card selection
    onAddCard: (plotLineId: number, chapterId: number) => void;       // Add a new card at position
    onMoveCard?: (cardId: number, newPlotLineId: number, newChapterId: number, newPosition?: number) => void; // Move card
    onEditCard?: (card: PlotCard) => void;                            // Edit card
    onDeleteCard?: (card: PlotCard) => void;                          // Delete card
    onCopyCard?: (card: PlotCard) => void;                            // Duplicate card
    onShowDetails?: (card: PlotCard) => void;                         // Show card details

    // Selection state
    selectedItems: PlotCard[];            // Currently selected cards

    // Display options
    compactMode?: boolean;                // Whether to show cards in compact mode

    // Chapter action handlers
    onEditChapter?: (chapterId: number, data: { name: string; description: string; outline?: string }) => void;
    onDeleteChapter?: (chapterId: number) => void;
    onAddChapter?: () => void;

    // Plot line action handlers
    onEditPlotLine?: (plotLineId: number, data: { title: string }) => void;
    onDeletePlotLine?: (plotLineId: number) => void;
    onApplyTemplate?: (timelineId: number) => void;
    onImproveTimeline?: (timelineId: number, timelineName: string) => void;

    // Selection mode props
    selectedChapters?: Chapter[];                 // Currently selected chapters
    selectedTimelines?: PlotLine[];               // Currently selected plot lines
    onChapterSelect?: (chapter: Chapter) => void; // Handle chapter selection
    onTimelineSelect?: (plotLine: PlotLine) => void; // Handle plot line selection
    onReorderChapters?: () => void;
    onReorderTimelines?: () => void;
}

/**
 * Main timeline grid component
 * Renders the timeline with plot lines, chapters, and cards
 */
export const TimelineGrid: React.FC<TimelineGridProps> = ({
    plotLines,
    chapters,
    cards,
    onCardSelect,
    onAddCard,
    onMoveCard = () => { },
    onEditCard,
    onDeleteCard,
    onCopyCard,
    onShowDetails,
    selectedItems,
    compactMode = false,
    onEditChapter,
    onDeleteChapter,
    onAddChapter,
    onEditPlotLine,
    onDeletePlotLine,
    onApplyTemplate,
    onImproveTimeline,
    // Selection mode props
    selectedChapters = [],
    selectedTimelines = [],
    onChapterSelect,
    onTimelineSelect,
    onReorderChapters,
    onReorderTimelines
}) => {
    // Dialog store for opening dialogs
    const { openDialog } = useDialogStore();

    // Chapter deletion confirmation state
    const [confirmDeleteChapterId, setConfirmDeleteChapterId] = useState<number | null>(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

    // Context menu state
    const [contextChapter, setContextChapter] = useState<Chapter | null>(null);
    const [contextCard, setContextCard] = useState<PlotCard | null>(null);

    // Plot line deletion confirmation state
    const [confirmDeletePlotLineId, setConfirmDeletePlotLineId] = useState<number | null>(null);
    const [isConfirmDeletePlotLineOpen, setIsConfirmDeletePlotLineOpen] = useState(false);

    // Sort chapters by _order if available, otherwise by id
    const sortedChapters = [...chapters].sort((a, b) => {
        // If both have _order, sort by it
        if (a._order !== null && b._order !== null &&
            a._order !== undefined && b._order !== undefined) {
            return a._order - b._order;
        }
        // If only one has _order, put the one with _order first
        if (a._order !== null && a._order !== undefined) return -1;
        if (b._order !== null && b._order !== undefined) return 1;
        // Otherwise sort by created_at
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });

    // Sort plot lines by _order if available, otherwise by id
    const sortedPlotLines = [...plotLines].sort((a, b) => {
        // If both have _order, sort by it
        if (a._order !== null && b._order !== null &&
            a._order !== undefined && b._order !== undefined) {
            return a._order - b._order;
        }
        // If only one has _order, put the one with _order first
        if (a._order !== null && a._order !== undefined) return -1;
        if (b._order !== null && b._order !== undefined) return 1;
        // Otherwise sort by id or created_at
        if (a.created_at && b.created_at) {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }
        return a.id - b.id;
    });

    const handleEditChapter = async (chapter: Chapter) => {
        if (!onEditChapter) return;

        console.log('=== EDIT CHAPTER DEBUG ===');
        console.log('Full chapter object:', chapter);
        console.log('Chapter name:', chapter.name);
        console.log('Chapter title:', chapter.title);
        console.log('Chapter description:', chapter.description);
        console.log('Chapter description type:', typeof chapter.description);
        console.log('Chapter description length:', chapter.description?.length);

        // Load existing outline if available
        let existingOutline = '';
        try {
            const { getChapterOutline } = await import('@/services/supabase/chapter-outlines');
            const outline = await getChapterOutline(chapter.id);
            existingOutline = outline?.content || '';
            console.log('Loaded existing outline:', existingOutline);
        } catch (error) {
            console.warn('Could not load chapter outline:', error);
        }

        const defaultValues = {
            name: chapter.name || chapter.title || '',
            description: chapter.description || '',
            outline: existingOutline
        };

        console.log('Default values being passed to dialog:', defaultValues);
        console.log('=== END EDIT CHAPTER DEBUG ===');

        openDialog('form', {
            title: 'Edit Chapter',
            description: 'Update your chapter details and outline',
            submitText: 'Update Chapter',
            fields: [
                { name: 'name', label: 'Chapter Name', type: 'text', required: true, defaultValue: defaultValues.name },
                { name: 'description', label: 'Description', type: 'textarea', defaultValue: defaultValues.description },
                { name: 'outline', label: 'Chapter Outline', type: 'textarea', defaultValue: defaultValues.outline }
            ]
        }, (data: { name: string; description: string; outline?: string }) => {
            onEditChapter(chapter.id, data);
        });
    };

    // This function is used for the EnhancedContextMenu
    const handleEditChapterFromContext = async (chapterId: number, _data: { name: string; description: string }) => {
        if (!onEditChapter) return;

        // Find the chapter by ID
        const chapterToEdit = chapters.find(c => c.id === chapterId);
        if (!chapterToEdit) {
            console.error('Chapter not found for ID:', chapterId);
            return;
        }

        // Load existing outline if available
        let existingOutline = '';
        try {
            const { getChapterOutline } = await import('@/services/supabase/chapter-outlines');
            const outline = await getChapterOutline(chapterId);
            existingOutline = outline?.content || '';
        } catch (error) {
            console.warn('Could not load chapter outline:', error);
        }

        // Use openDialog to open the edit dialog
        openDialog('form', {
            title: 'Edit Chapter',
            description: 'Update your chapter details and outline',
            submitText: 'Update Chapter',
            fields: [
                { name: 'name', label: 'Chapter Name', type: 'text', required: true, defaultValue: chapterToEdit.name || chapterToEdit.title || '' },
                { name: 'description', label: 'Description', type: 'textarea', defaultValue: chapterToEdit.description || '' },
                { name: 'outline', label: 'Chapter Outline', type: 'textarea', defaultValue: existingOutline }
            ]
        }, (updatedData: { name: string; description: string; outline?: string }) => {
            onEditChapter(chapterId, updatedData);
        });

        // Reset context after action
        setContextChapter(null);
    };

    const handleDeleteClick = (chapterId: number) => {
        setConfirmDeleteChapterId(chapterId);
        setIsConfirmDialogOpen(true);
        // Reset context after action
        setContextChapter(null);
    };

    const handleConfirmDelete = () => {
        if (confirmDeleteChapterId !== null && onDeleteChapter) {
            onDeleteChapter(confirmDeleteChapterId);
            setConfirmDeleteChapterId(null);
        }
        setIsConfirmDialogOpen(false);
    };

    const handleCancelDelete = () => {
        setConfirmDeleteChapterId(null);
        setIsConfirmDialogOpen(false);
    };

    const handleChapterContextMenu = useCallback((chapter: Chapter, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling
        console.log('Chapter context menu triggered for:', chapter);
        setContextChapter(chapter);
        setContextCard(null);
    }, []);

    const handleCardContextMenu = useCallback((card: PlotCard, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling
        console.log('Card context menu triggered for:', card);
        setContextCard(card);
        setContextChapter(null);
    }, []);

    const handleBackgroundContextMenu = useCallback((e: React.MouseEvent) => {
        // Reset context regardless of where the click happened
        e.preventDefault();
        setContextChapter(null);
        setContextCard(null);
    }, []);

    // Add a click handler to reset context when clicking anywhere
    const handleBackgroundClick = useCallback(() => {
        setContextChapter(null);
        setContextCard(null);
    }, []);

    // Add handlers for plot line editing and deletion
    const handleEditPlotLine = (plotLine: PlotLine) => {
        if (!onEditPlotLine) return;

        openDialog('form', {
            title: 'Edit Plot Line',
            description: 'Update plot line details',
            submitText: 'Update Plot Line',
            fields: [
                { name: 'title', label: 'Timeline Name', type: 'text', required: true, defaultValue: plotLine.name }
            ]
        }, (updatedData: { title: string }) => {
            onEditPlotLine(plotLine.id, updatedData);
        });
    };

    const handleDeletePlotLine = (plotLineId: number) => {
        if (!onDeletePlotLine) return;

        // Confirm deletion
        setConfirmDeletePlotLineId(plotLineId);
        setIsConfirmDeletePlotLineOpen(true);
    };

    const handleConfirmDeletePlotLine = () => {
        if (confirmDeletePlotLineId !== null && onDeletePlotLine) {
            onDeletePlotLine(confirmDeletePlotLineId);
            setConfirmDeletePlotLineId(null);
        }
        setIsConfirmDeletePlotLineOpen(false);
    };

    const handleCancelDeletePlotLine = () => {
        setConfirmDeletePlotLineId(null);
        setIsConfirmDeletePlotLineOpen(false);
    };

    if (plotLines.length === 0 || chapters.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 bg-card rounded-lg p-6 border">
                <h3 className="text-lg font-medium mb-2">No Timeline Data</h3>
                <p className="text-sm text-muted-foreground mb-4 text-center">
                    Start by adding plot lines and chapters to your timeline.
                </p>
            </div>
        );
    }

    return (
        <EnhancedContextMenu
            addPlotLine={() => { }}
            addChapter={onAddChapter || (() => { })}
            selectedItems={selectedItems}
            onEditChapter={handleEditChapterFromContext}
            onDeleteChapter={onDeleteChapter}
            currentChapter={contextChapter || undefined}
            onEditCard={onEditCard}
            onDeleteCard={onDeleteCard}
            onCopyCard={onCopyCard}
            currentCard={contextCard || undefined}
        >
            <div
                className="w-full overflow-auto scrollbar-hide rounded-2xl border border-border/60 bg-card shadow-sm"
                onContextMenu={handleBackgroundContextMenu}
                onClick={handleBackgroundClick}
            >
                <motion.div
                    className="grid"
                    variants={container}
                    initial="hidden"
                    animate="show"
                    style={{
                        gridTemplateColumns: `auto repeat(${chapters.length}, minmax(280px, 1fr))`,
                        minWidth: Math.max(chapters.length * 280, 800) + 'px'
                    }}
                >
                    {/* Header row */}
                    <div className="sticky top-0 left-0 z-30 bg-card border-b border-border/60">
                        <div className="px-4 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider flex items-center justify-between">
                            <span>Plot Lines</span>
                        </div>
                    </div>
                    {sortedChapters.map(chapter => (
                        <div
                            key={chapter.id}
                            className={cn(
                                "sticky top-0 z-20 bg-card border-b border-l border-border/60 group hover:bg-muted/5 transition-colors",
                                selectedChapters.some(c => c.id === chapter.id) && "bg-primary/5"
                            )}
                            onContextMenu={(e) => handleChapterContextMenu(chapter, e)}
                            data-chapter-id={chapter.id}
                            data-chapter-name={chapter.name}
                        >
                            <div
                                className="px-3 py-2.5"
                                onContextMenu={(e) => handleChapterContextMenu(chapter, e)}
                            >
                                <div
                                    className="flex items-center justify-between"
                                    onContextMenu={(e) => handleChapterContextMenu(chapter, e)}
                                >
                                    <div
                                        className="font-medium truncate text-sm flex items-center gap-2 text-foreground/90"
                                        title={chapter.name || chapter.title}
                                        onContextMenu={(e) => handleChapterContextMenu(chapter, e)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onChapterSelect) onChapterSelect(chapter);
                                        }}
                                    >
                                        {/* Always show checkbox when in selection mode, or when this chapter is selected */}
                                        {(selectedChapters.length > 0 || selectedChapters.some(c => c.id === chapter.id)) && (
                                            <div
                                                className={cn(
                                                    "flex-shrink-0 h-4 w-4 rounded-sm flex items-center justify-center transition-all",
                                                    selectedChapters.some(c => c.id === chapter.id)
                                                        ? "bg-primary text-primary-foreground"
                                                        : "border border-border"
                                                )}
                                            >
                                                {selectedChapters.some(c => c.id === chapter.id) && (
                                                    <Check className="h-3 w-3" />
                                                )}
                                            </div>
                                        )}
                                        <span>{chapter.name || chapter.title}</span>
                                    </div>
                                    {(onEditChapter || onDeleteChapter) && (
                                        <div className="flex items-center space-x-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-6 w-6"
                                                                >
                                                                    <MoreHorizontal className="h-3 w-3" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end" className="w-48">
                                                                {onChapterSelect && (
                                                                    <DropdownMenuItem
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onChapterSelect(chapter);
                                                                        }}
                                                                        className="flex items-center cursor-pointer"
                                                                    >
                                                                        <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                                                        <span>Select</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {onEditChapter && (
                                                                    <DropdownMenuItem
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditChapter(chapter);
                                                                        }}
                                                                        className="flex items-center cursor-pointer"
                                                                    >
                                                                        <Edit className="h-3.5 w-3.5 mr-2" />
                                                                        <span>Edit Chapter</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {onDeleteChapter && (
                                                                    <DropdownMenuItem
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteClick(chapter.id);
                                                                        }}
                                                                        className="flex items-center cursor-pointer text-destructive"
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5 mr-2" />
                                                                        <span>Remove Chapter</span>
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Chapter Actions</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate mt-1" title={chapter.description}>
                                    {chapter.description}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Plot lines and cards */}
                    {sortedPlotLines.map((plotLine, plotIndex) => (
                        <React.Fragment key={plotLine.id}>
                            {/* Plot line name */}
                            <motion.div
                                variants={item}
                                className={cn(
                                    "sticky left-0 z-10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/75",
                                    "px-4 py-3 border-b border-border flex items-center justify-between group hover:bg-muted/5 transition-colors",
                                    plotIndex === plotLines.length - 1 ? "border-b-0" : "",
                                    selectedTimelines.some(t => t.id === plotLine.id) && "bg-primary/5"
                                )}
                            >
                                <div
                                    className="flex items-center gap-2 text-foreground/90"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onTimelineSelect) onTimelineSelect(plotLine);
                                    }}
                                >
                                    {/* Always show checkbox when in selection mode, or when this timeline is selected */}
                                    {(selectedTimelines.length > 0 || selectedTimelines.some(t => t.id === plotLine.id)) && (
                                        <div
                                            className={cn(
                                                "flex-shrink-0 h-4 w-4 rounded-sm flex items-center justify-center transition-all",
                                                selectedTimelines.some(t => t.id === plotLine.id)
                                                    ? "bg-primary text-primary-foreground"
                                                    : "border border-border"
                                            )}
                                        >
                                            {selectedTimelines.some(t => t.id === plotLine.id) && (
                                                <Check className="h-3 w-3" />
                                            )}
                                        </div>
                                    )}
                                    <div className="font-medium truncate text-sm" title={plotLine.name}>
                                        {plotLine.name}
                                    </div>
                                </div>
                                {(onEditPlotLine || onDeletePlotLine || onApplyTemplate) && (
                                    <div className="flex items-center space-x-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6"
                                                            >
                                                                <MoreHorizontal className="h-3 w-3" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-48">
                                                            {onTimelineSelect && (
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onTimelineSelect(plotLine);
                                                                    }}
                                                                    className="flex items-center cursor-pointer"
                                                                >
                                                                    <CheckSquare className="h-3.5 w-3.5 mr-2" />
                                                                    <span>Select</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                            {onEditPlotLine && (
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditPlotLine(plotLine);
                                                                    }}
                                                                    className="flex items-center cursor-pointer"
                                                                >
                                                                    <Edit className="h-3.5 w-3.5 mr-2" />
                                                                    <span>Edit Plot Line</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                            {onApplyTemplate && (
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onApplyTemplate(plotLine.id);
                                                                    }}
                                                                    className="flex items-center cursor-pointer"
                                                                >
                                                                    <FileText className="h-3.5 w-3.5 mr-2" />
                                                                    <span>Apply Template</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                            {onImproveTimeline && (
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onImproveTimeline(plotLine.id, plotLine.name);
                                                                    }}
                                                                    className="flex items-center cursor-pointer"
                                                                >
                                                                    <Sparkles className="h-3.5 w-3.5 mr-2" />
                                                                    <span>Improve with AI</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                            {onDeletePlotLine && (
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeletePlotLine(plotLine.id);
                                                                    }}
                                                                    className="flex items-center cursor-pointer text-destructive"
                                                                >
                                                                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                                                                    <span>Remove Plot Line</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Plot Line Actions</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                )}
                            </motion.div>

                            {/* Cards for each chapter */}
                            {sortedChapters.map(chapter => {
                                // Ensure we prioritize chapter_id over y for filtering
                                const cardsInCell = cards.filter(card => {
                                    const cardChapterId = card.chapter_id !== null && card.chapter_id !== undefined
                                        ? card.chapter_id
                                        : card.y;

                                    return card.x === plotLine.id && cardChapterId === chapter.id;
                                });

                                // console.log(`=== Cards for plotLine ${plotLine.id}, chapter ${chapter.id}:`,
                                //     cardsInCell.length,
                                //     cardsInCell.map(c => ({
                                //         id: c.id,
                                //         title: c.title,
                                //         chapter_id: c.chapter_id,
                                //         y: c.y,
                                //         position: c.position
                                //     }))
                                // );

                                return (
                                    <DroppableCell
                                        key={`${plotLine.id}-${chapter.id}`}
                                        plotLineId={plotLine.id}
                                        chapterId={chapter.id}
                                        cards={cardsInCell}
                                        selectedItems={selectedItems}
                                        compactMode={compactMode}
                                        onCardSelect={onCardSelect}
                                        onAddCard={onAddCard}
                                        onMoveCard={onMoveCard}
                                        onEditCard={onEditCard}
                                        onDeleteCard={onDeleteCard}
                                        onCopyCard={onCopyCard}
                                        onShowDetails={onShowDetails}
                                        isLastRow={plotIndex === plotLines.length - 1}
                                        onCardContextMenu={handleCardContextMenu}
                                    />
                                );
                            })}
                        </React.Fragment>
                    ))}
                </motion.div>

                {/* Confirmation Dialog for Chapter Deletion */}
                <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Remove Chapter</DialogTitle>
                            <DialogDescription>
                                {confirmDeleteChapterId !== null && (
                                    <>
                                        Are you sure you want to remove "
                                        {chapters.find(c => c.id === confirmDeleteChapterId)?.name || chapters.find(c => c.id === confirmDeleteChapterId)?.title}"?
                                        This will also delete all cards associated with this chapter.
                                    </>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={handleCancelDelete}>
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleConfirmDelete}>
                                Remove
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Confirmation Dialog for Plot Line Deletion */}
                <Dialog open={isConfirmDeletePlotLineOpen} onOpenChange={setIsConfirmDeletePlotLineOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Remove Plot Line</DialogTitle>
                            <DialogDescription>
                                {confirmDeletePlotLineId !== null && (
                                    <>
                                        Are you sure you want to remove "
                                        {plotLines.find(p => p.id === confirmDeletePlotLineId)?.name}"?
                                        This will also delete all cards associated with this plot line.
                                    </>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={handleCancelDeletePlotLine}>
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleConfirmDeletePlotLine}>
                                Remove
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div >
        </EnhancedContextMenu >
    );
};